"use client";

import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { calculateApiNodeId, computeNodeStepNumbers, extractWorkflowVariables, getFallbackVariableDisplayLabel, isValidVariable, resolveVariableValue } from "./extract-workflow-variables";
import { getFriendlyLabel, getPropKey } from "@/components/ui/workflow-step-row/components/VariablePill";
import { usePropValues } from "../PropValuesContext";
import { useRunExecutionStore } from "@/stores/runExecutionStore";
import { useInputSchema } from "./InputSchemaContext";
import { getCurrentWordInContentEditable, getVariableBeforeCursor, insertVariableAtCursor, parseStringToFragment, serializeContentEditable, VARIABLE_ATTR } from "./variable-textarea-utils";
import { VariablePillPopover } from "./VariablePillPopover";
import MarkdownToolbar from "./MarkdownToolbar";
import dynamic from "next/dynamic";
const ReactMarkdown = dynamic(() => import("react-markdown"), {
  ssr: false
});
import remarkGfm from "remark-gfm";
import type { Edge, Node } from "@xyflow/react";
interface NodeDefinition {
  type: string;
  resultSchema?: {
    properties?: Record<string, {
      description?: string;
      type?: string;
    }>;
  };
}
interface VariableTextareaProps {
  label: string;
  description?: string;
  value: string | number | Record<string, unknown> | unknown[] | null | undefined;
  onChange: (value: string) => void;
  required?: boolean;
  currentNodeId?: string;
  allNodes?: Node[];
  allEdges?: Edge[];
  nodeDefinitions?: NodeDefinition[];
  maxLength?: number;
  showMarkdownToolbar?: boolean;
}
export default function VariableTextarea({
  label,
  description,
  value: rawValue,
  onChange,
  required = false,
  currentNodeId = "",
  allNodes = [],
  allEdges = [],
  nodeDefinitions = [],
  maxLength,
  showMarkdownToolbar = false
}: VariableTextareaProps) {
  // Safety: workflow variable resolution can pass non-string values (objects, arrays, numbers)
  const value = typeof rawValue === "string" ? rawValue : rawValue == null ? "" : typeof rawValue === "object" ? JSON.stringify(rawValue) : String(rawValue);
  const inputSchema = useInputSchema();
  const propValues = usePropValues();
  const nodeStates = useRunExecutionStore(state => state.currentRun?.nodeStates);
  const editableRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPlacement, setDropdownPlacement] = useState<"above" | "below">("below");
  const [currentTrigger, setCurrentTrigger] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const commandListRef = useRef<HTMLDivElement>(null);
  const isUpdatingRef = useRef(false);
  const lastValueRef = useRef(value);
  const [activePill, setActivePill] = useState<HTMLElement | null>(null);
  const [isPreview, setIsPreview] = useState(false);

  // Compute step numbers for display labels (BFS from root)
  const stepNumbers = useMemo(() => computeNodeStepNumbers(allNodes, allEdges), [allNodes, allEdges]);

  // Extract available variables from workflow (flat structure for inline dropdown and validation)
  const availableVariables = useMemo(() => {
    if (!currentNodeId || allNodes.length === 0) {
      return [];
    }
    return extractWorkflowVariables(currentNodeId, allNodes, allEdges, nodeDefinitions, undefined, inputSchema, stepNumbers);
  }, [currentNodeId, allNodes, allEdges, nodeDefinitions, inputSchema, stepNumbers]);

  // Build lookup: variableId -> displayLabel
  const displayLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const v of availableVariables) {
      if (v.displayLabel) map.set(v.id, v.displayLabel);
    }
    return map;
  }, [availableVariables]);

  // Callback to get display text for a variable (friendly name + optional run value)
  const getDisplayText = useCallback((variableId: string): string | undefined => {
    const inner = variableId.slice(2, -2);
    let label_0 = displayLabelMap.get(variableId);
    // Fallback: compute friendly label for $ and @ variables not in the extracted list
    if (!label_0) {
      label_0 = getFriendlyLabel(inner) ?? getFallbackVariableDisplayLabel(variableId, allNodes, allEdges, stepNumbers) ?? undefined;
    }
    if (!label_0) return undefined;

    // For $props variables, show the resolved value alongside the prop name
    const propKey = getPropKey(inner);
    if (propKey && propValues) {
      const val = propValues[propKey];
      if (val !== undefined && val !== null && val !== "") {
        return `${propKey}: ${String(val)}`;
      }
    }
    const runVal = resolveVariableValue(variableId, nodeStates);
    return runVal != null ? `${label_0} = ${runVal}` : label_0;
  }, [displayLabelMap, nodeStates, propValues, allNodes, allEdges, stepNumbers]);
  const isRenderableVariable = useCallback((variableId_0: string) => isValidVariable(variableId_0, availableVariables) || getFallbackVariableDisplayLabel(variableId_0, allNodes, allEdges, stepNumbers) !== undefined, [availableVariables, allNodes, allEdges, stepNumbers]);

  // Sync DOM with value prop when it changes externally
  useEffect(() => {
    if (!editableRef.current || isUpdatingRef.current) return;

    // Only update DOM if value actually changed from external source
    const currentContent = serializeContentEditable(editableRef.current);
    if (currentContent === value) {
      lastValueRef.current = value;
      return;
    }

    // Clear and rebuild content
    editableRef.current.innerHTML = "";
    setActivePill(null); // Clear stale DOM reference
    const fragment = parseStringToFragment(value, availableVariables, isRenderableVariable, getDisplayText);
    editableRef.current.appendChild(fragment);
    lastValueRef.current = value;
  }, [value, availableVariables, getDisplayText, isRenderableVariable]);

  // Refresh pill display text when run values change (nodeStates updates)
  useEffect(() => {
    if (!editableRef.current) return;
    const pills = editableRef.current.querySelectorAll(`[data-variable]`);
    pills.forEach(pill => {
      const varId = pill.getAttribute("data-variable");
      if (varId) {
        const newText = getDisplayText(varId) || varId;
        // Update the text span child (first child), not the whole pill
        const textSpan = pill.querySelector(".variable-text");
        const target = textSpan || pill;
        if (target.textContent !== newText) {
          target.textContent = newText;
        }
      }
    });
  }, [getDisplayText]);

  // Handle input changes
  const handleInput = useCallback(() => {
    if (!editableRef.current) return;
    isUpdatingRef.current = true;

    // Serialize content and call onChange
    const newValue = serializeContentEditable(editableRef.current);

    // Enforce maxLength - truncate if exceeded
    if (maxLength && newValue.length > maxLength) {
      const truncatedValue = newValue.slice(0, maxLength);
      editableRef.current.innerHTML = "";
      const fragment_0 = parseStringToFragment(truncatedValue, availableVariables, isRenderableVariable, getDisplayText);
      editableRef.current.appendChild(fragment_0);

      // Move cursor to end
      const selection = window.getSelection();
      if (selection && editableRef.current.lastChild) {
        const range = document.createRange();
        range.selectNodeContents(editableRef.current);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      lastValueRef.current = truncatedValue;
      onChange(truncatedValue);
      isUpdatingRef.current = false;
      return;
    }
    if (newValue !== lastValueRef.current) {
      lastValueRef.current = newValue;
      onChange(newValue);
    }

    // Check for variable trigger
    const {
      word
    } = getCurrentWordInContentEditable(editableRef.current);
    if (word.startsWith("{{")) {
      // Check available space for dropdown placement
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - containerRect.bottom;
        const spaceAbove = containerRect.top;
        const showAbove = spaceBelow < 220 && spaceAbove > 220;
        setDropdownPlacement(showAbove ? "above" : "below");
      }
      setDropdownVisible(true);
      setCurrentTrigger(word);
    } else {
      setDropdownVisible(false);
      setCurrentTrigger("");
    }
    isUpdatingRef.current = false;
  }, [onChange, maxLength, availableVariables, getDisplayText, isRenderableVariable]);

  // Handle variable selection from dropdown
  const onVariableSelect = useCallback((variableId_1: string) => {
    if (!editableRef.current) return;
    const isValid = isRenderableVariable(variableId_1);
    const displayText = getDisplayText(variableId_1);
    insertVariableAtCursor(editableRef.current, variableId_1, isValid, displayText);
    setDropdownVisible(false);
    setCurrentTrigger("");

    // Trigger onChange with new value
    const newValue_0 = serializeContentEditable(editableRef.current);
    lastValueRef.current = newValue_0;
    onChange(newValue_0);

    // Refocus
    editableRef.current.focus();
  }, [onChange, getDisplayText, isRenderableVariable]);

  // Remove a variable span and its adjacent ZWSP, then serialize and call onChange
  const removeVariableSpan = useCallback((variableSpan: HTMLElement) => {
    if (!editableRef.current) return;
    const parent = variableSpan.parentNode;
    if (!parent) return;
    const nextSibling = variableSpan.nextSibling;
    if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE && (nextSibling.textContent || "").replace(/[\u200B\s]/g, "") === "") {
      nextSibling.remove();
    }
    variableSpan.remove();
    const newValue_1 = serializeContentEditable(editableRef.current);
    lastValueRef.current = newValue_1;
    onChange(newValue_1);
  }, [onChange]);

  // Delegated click handler — detect clicks on variable pills to show popover
  const handleEditableClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target_0 = e.target as HTMLElement;
    const pill_0 = target_0.closest(`[${VARIABLE_ATTR}]`) as HTMLElement | null;
    if (pill_0) {
      e.preventDefault();
      e.stopPropagation();
      setActivePill(prev => prev === pill_0 ? null : pill_0);
    }
  }, []);

  // Filter variables for dropdown (must be before handleKeyDown which uses it)
  const searchQuery = currentTrigger.replace("{{", "").toLowerCase();
  const filteredVariables = useMemo(() => availableVariables.filter(v_0 => {
    if (!searchQuery) return true;
    // Extract field name: "{{root.result.price}}" → "price"
    const inner_0 = v_0.id.slice(2, -2);
    const fieldName = inner_0.split(".").pop()?.toLowerCase() || "";
    return fieldName.includes(searchQuery) || v_0.label.toLowerCase().includes(searchQuery) || (v_0.displayLabel?.toLowerCase().includes(searchQuery) ?? false);
  }), [searchQuery, availableVariables]);

  // Handle keydown events
  const handleKeyDown = useCallback((e_0: React.KeyboardEvent<HTMLDivElement>) => {
    // Keyboard navigation when dropdown is visible
    if (dropdownVisible && filteredVariables.length > 0) {
      if (e_0.key === "ArrowDown") {
        e_0.preventDefault();
        setSelectedIndex(prev_0 => (prev_0 + 1) % filteredVariables.length);
        return;
      }
      if (e_0.key === "ArrowUp") {
        e_0.preventDefault();
        setSelectedIndex(prev_1 => (prev_1 - 1 + filteredVariables.length) % filteredVariables.length);
        return;
      }
      if (e_0.key === "Enter" || e_0.key === "Tab") {
        e_0.preventDefault();
        const selected = filteredVariables[selectedIndex] ?? filteredVariables[0];
        if (selected) onVariableSelect(selected.id);
        return;
      }
    }

    // Auto-continue lists on Enter (only for markdown-textarea)
    if (showMarkdownToolbar && e_0.key === "Enter" && editableRef.current && !dropdownVisible) {
      // Get the current line by reading the text node the cursor is in.
      // contentEditable may use text nodes with \n (pre-wrap) or <div>/<br> for lines.
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && sel.isCollapsed) {
        const cursorNode = sel.anchorNode;
        const cursorOffset = sel.anchorOffset;

        // Get the text of the current line by finding text content around cursor
        let lineText = "";
        if (cursorNode && cursorNode.nodeType === Node.TEXT_NODE) {
          const fullText = cursorNode.textContent || "";
          // Find start of current line (search backward for \n)
          let lineStart = fullText.lastIndexOf("\n", cursorOffset - 1) + 1;
          lineText = fullText.slice(lineStart, cursorOffset);
        } else if (cursorNode === editableRef.current || cursorNode?.parentNode === editableRef.current) {
          // Cursor is between child nodes — get the text of the previous sibling
          const children = Array.from(editableRef.current.childNodes);
          const idx = cursorNode === editableRef.current ? cursorOffset - 1 : children.indexOf(cursorNode as ChildNode);
          if (idx >= 0 && children[idx]) {
            lineText = children[idx].textContent?.replace(/\u200B/g, "") || "";
          }
        }

        // Check for numbered list: "N. text"
        const numberedMatch = lineText.match(/^(\d+)\.\s(.*)$/);
        if (numberedMatch) {
          e_0.preventDefault();
          const num = parseInt(numberedMatch[1], 10);
          const content = numberedMatch[2];
          if (!content) {
            // Empty item — remove the empty list prefix
            const fullSerialized = serializeContentEditable(editableRef.current);
            const withoutLast = fullSerialized.replace(/\n\d+\.\s*$/, "");
            editableRef.current.innerHTML = "";
            const fragment_1 = parseStringToFragment(withoutLast, availableVariables, isValidVariable, getDisplayText);
            editableRef.current.appendChild(fragment_1);
            // Restore cursor to end
            const sel_0 = window.getSelection();
            if (sel_0) {
              const range_0 = document.createRange();
              range_0.selectNodeContents(editableRef.current);
              range_0.collapse(false);
              sel_0.removeAllRanges();
              sel_0.addRange(range_0);
            }
          } else {
            // Insert newline + next number.
            // TODO: execCommand is deprecated — migrate to Selection.getRangeAt().insertNode()
            // when browser/IME compatibility allows (see MDN execCommand deprecation notice).
            document.execCommand("insertText", false, `\n${num + 1}. `);
          }
          handleInput();
          return;
        }

        // Check for bullet list: "- text"
        const bulletMatch = lineText.match(/^-\s(.*)$/);
        if (bulletMatch) {
          e_0.preventDefault();
          const content_0 = bulletMatch[1];
          if (!content_0) {
            const fullSerialized_0 = serializeContentEditable(editableRef.current);
            const withoutLast_0 = fullSerialized_0.replace(/\n-\s*$/, "");
            editableRef.current.innerHTML = "";
            const fragment_2 = parseStringToFragment(withoutLast_0, availableVariables, isValidVariable, getDisplayText);
            editableRef.current.appendChild(fragment_2);
            // Restore cursor to end
            const sel_1 = window.getSelection();
            if (sel_1) {
              const range_1 = document.createRange();
              range_1.selectNodeContents(editableRef.current);
              range_1.collapse(false);
              sel_1.removeAllRanges();
              sel_1.addRange(range_1);
            }
          } else {
            document.execCommand("insertText", false, "\n- ");
          }
          handleInput();
          return;
        }
      }
    }

    // Handle backspace on variables
    if (e_0.key === "Backspace" && editableRef.current) {
      const variableSpan_0 = getVariableBeforeCursor(editableRef.current);
      if (variableSpan_0) {
        e_0.preventDefault();
        const parent_0 = variableSpan_0.parentNode;
        if (parent_0) {
          const prevSibling = variableSpan_0.previousSibling;
          removeVariableSpan(variableSpan_0);

          // Position cursor where variable was (after DOM mutation)
          const range_2 = document.createRange();
          const selection_0 = window.getSelection();
          if (prevSibling && prevSibling.isConnected) {
            range_2.setStartAfter(prevSibling);
          } else {
            range_2.setStart(parent_0, 0);
          }
          range_2.collapse(true);
          selection_0?.removeAllRanges();
          selection_0?.addRange(range_2);
        }
        return;
      }
    }

    // Handle dropdown dismiss
    if (dropdownVisible && e_0.key === "Escape") {
      e_0.preventDefault();
      setDropdownVisible(false);
      setCurrentTrigger("");
    }
  }, [dropdownVisible, filteredVariables, selectedIndex, onVariableSelect, removeVariableSpan, showMarkdownToolbar, onChange, availableVariables, isValidVariable, getDisplayText, handleInput]);

  // Handle paste - strip HTML and parse variables
  const handlePaste = useCallback((e_1: React.ClipboardEvent<HTMLDivElement>) => {
    e_1.preventDefault();
    const text = e_1.clipboardData.getData("text/plain");
    if (!text || !editableRef.current) return;

    // Insert plain text at cursor
    const selection_1 = window.getSelection();
    if (!selection_1 || selection_1.rangeCount === 0) return;
    const range_3 = selection_1.getRangeAt(0);
    range_3.deleteContents();

    // Parse the pasted text for variables
    const fragment_3 = parseStringToFragment(text, availableVariables, isRenderableVariable, getDisplayText);
    range_3.insertNode(fragment_3);

    // Move cursor to end of pasted content
    range_3.collapse(false);
    selection_1.removeAllRanges();
    selection_1.addRange(range_3);

    // Trigger onChange
    const newValue_2 = serializeContentEditable(editableRef.current);
    lastValueRef.current = newValue_2;
    onChange(newValue_2);
  }, [onChange, availableVariables, getDisplayText, isRenderableVariable]);

  // Handle blur
  const handleBlur = useCallback(() => {
    // Delay to allow dropdown clicks
    setTimeout(() => {
      setDropdownVisible(false);
      setCurrentTrigger("");
    }, 200);
  }, []);

  // Prevent dropdown blur
  const handleDropdownMouseDown = useCallback((e_2: React.MouseEvent) => {
    e_2.preventDefault();
    e_2.stopPropagation();
  }, []);

  // Called by MarkdownToolbar after inserting formatting syntax
  const handleToolbarContentChange = useCallback(() => {
    if (!editableRef.current) return;
    const newValue_3 = serializeContentEditable(editableRef.current);
    if (newValue_3 !== lastValueRef.current) {
      lastValueRef.current = newValue_3;
      onChange(newValue_3);
    }
  }, [onChange]);

  // Reset keyboard selection when search query changes or dropdown opens
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery, dropdownVisible]);

  // Scroll selected item into view
  useEffect(() => {
    if (!dropdownVisible || !commandListRef.current) return;
    const selected_0 = commandListRef.current.querySelector('[data-highlighted="true"]');
    selected_0?.scrollIntoView({
      block: "nearest"
    });
  }, [selectedIndex, dropdownVisible]);

  // Extract unique source nodes from variable references in the current value
  const usedVariableSources = useMemo(() => {
    if (!value || typeof value !== "string" || allNodes.length === 0) return [];

    // Match all variable patterns like {{nodeId.result.field}} or {{nodeId.payload.field}}
    const variableRegex = /\{\{([^.}]+)\.[^}]+\}\}/g;
    const nodeIds = new Set<string>();
    let match;
    while ((match = variableRegex.exec(value)) !== null) {
      const nodeId = match[1];
      // Skip special variables like $item, $index, $props, @field
      if (!nodeId.startsWith("$") && !nodeId.startsWith("@")) {
        nodeIds.add(nodeId);
      }
    }

    // Map node IDs to node objects with display names
    // Use calculateApiNodeId to match variable IDs to nodes (same logic used when generating variables)
    return Array.from(nodeIds).map(varNodeId => {
      // Find the node by calculating its API node ID and comparing
      const node = allNodes.find(n => {
        const apiNodeId = calculateApiNodeId(n, allNodes, allEdges);
        return apiNodeId === varNodeId;
      });
      if (!node) return null;
      return {
        node,
        displayName: node.data.name as string || varNodeId,
        nodeId: varNodeId
      };
    }).filter((item): item is NonNullable<typeof item> => item !== null);
  }, [value, allNodes, allEdges]);
  return <div className="space-y-2">
      {label && <label className="field-label flex items-center gap-2">
          <span>{label}</span>
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}

      <div ref={containerRef} className="relative">
        <div className="border-ds-neutral-300 dark:border-ds-neutral-700 bg-ds-surface-card shadow-ds-soft relative rounded-lg border focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20">
          {showMarkdownToolbar && <MarkdownToolbar editableRef={editableRef} onContentChange={handleToolbarContentChange} isPreview={isPreview} onTogglePreview={() => setIsPreview(prev_2 => !prev_2)} />}
          {/* Contenteditable div — hidden when preview is active */}
          <div ref={editableRef} contentEditable suppressContentEditableWarning onInput={handleInput} onKeyDown={handleKeyDown} onPaste={handlePaste} onBlur={handleBlur} onClick={handleEditableClick} className="text-ds-text-primary empty:before:text-ds-text-disabled dark:empty:before:text-ds-text-muted min-h-[100px] w-full rounded-lg border-0 bg-transparent p-4 font-mono text-xs leading-loose caret-zinc-900 outline-none empty:before:content-[attr(data-placeholder)] dark:text-zinc-100 dark:caret-zinc-100" data-placeholder="Enter your message here. Type {{ to insert variables..." style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          display: isPreview ? "none" : undefined
        }} />
          {/* Markdown preview panel */}
          {isPreview && <div className="text-ds-text-primary [&_code]:bg-ds-neutral-100 [&_code]:dark:bg-ds-neutral-700 [&_pre]:bg-ds-neutral-100 [&_pre]:dark:bg-ds-neutral-700 [&_code]:text-2xs min-h-[100px] w-full p-4 text-xs leading-relaxed dark:text-zinc-100 [&_a]:text-blue-600 [&_a]:underline [&_a]:dark:text-blue-400 [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_del]:line-through [&_em]:italic [&_li]:ml-4 [&_ol]:list-decimal [&_p]:mb-1 [&_pre]:my-2 [&_pre]:rounded [&_pre]:p-2 [&_strong]:font-bold [&_ul]:list-disc">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {/* Convert single \n to double \n\n for CommonMark paragraph breaks,
                    but preserve list items and code blocks which need single \n */}
                {(value || "").replace(/\n(?!\n)/g, (match_0, offset, str) => {
              // Don't double newlines inside code blocks.
              // Known limitation: counts ``` occurrences to detect code blocks, so multiple
              // inline backtick spans can produce false positives. Impact is low — only affects
              // preview rendering, not the actual message content sent.
              const before = str.slice(0, offset);
              if ((before.match(/```/g) || []).length % 2 === 1) return match_0;
              // Don't double newlines between list items (- or N.)
              const nextChar = str.slice(offset + 1, offset + 4);
              const prevLine = before.slice(before.lastIndexOf("\n") + 1);
              if (nextChar.match(/^(\d+\.|-)/) && prevLine.match(/^(\d+\.|-)/)) return match_0;
              return "\n\n";
            })}
              </ReactMarkdown>
            </div>}
        </div>

        {/* Variable Suggestions Dropdown - Grouped by category */}
        {dropdownVisible && filteredVariables.length > 0 && <div ref={dropdownRef} onMouseDown={handleDropdownMouseDown} className={`absolute left-0 z-50 ${dropdownPlacement === "above" ? "bottom-full mb-1" : "top-full mt-1"}`}>
            <Command shouldFilter={false} className="border-ds-border-default bg-ds-surface-card max-h-[280px] w-[360px] overflow-auto rounded-lg border shadow-xl">
              <CommandList ref={commandListRef}>
                {Object.entries(filteredVariables.reduce((acc, v_1) => {
              if (!acc[v_1.category]) acc[v_1.category] = [];
              acc[v_1.category].push(v_1);
              return acc;
            }, {} as Record<string, typeof filteredVariables>)).map(([category, vars]) => <div key={category} className="border-ds-neutral-100 dark:border-ds-neutral-700/50 border-b last:border-0">
                    <div className="text-ds-text-secondary dark:bg-ds-neutral-900 sticky top-0 z-10 bg-zinc-50 px-3 py-2 text-xs font-semibold">
                      {category}
                    </div>
                    <CommandGroup>
                      {vars.map(variable => {
                  const runVal_0 = resolveVariableValue(variable.id, nodeStates);
                  return <CommandItem key={variable.id} value={variable.id} onSelect={() => onVariableSelect(variable.id)} data-highlighted={variable.id === filteredVariables[selectedIndex]?.id ? "true" : undefined} className={`cursor-pointer px-3 py-2 ${variable.id === filteredVariables[selectedIndex]?.id ? "dark:!bg-ds-neutral-700 !bg-blue-50" : ""}`}>
                            <div className="flex flex-col gap-1">
                              <span className="rounded-ds-xs inline-flex w-fit items-center gap-1.5 bg-gradient-to-r from-blue-50 to-indigo-100 px-2 py-1 font-mono text-xs text-blue-700 dark:from-blue-950/30 dark:to-indigo-950/30 dark:text-blue-400">
                                {variable.displayLabel || variable.id}
                              </span>
                              <span className="label-xs">{variable.label}</span>
                              {runVal_0 != null && <span className="font-mono text-xs text-green-600 dark:text-green-400">= {runVal_0}</span>}
                            </div>
                          </CommandItem>;
                })}
                    </CommandGroup>
                  </div>)}
              </CommandList>
            </Command>
          </div>}
      </div>

      {/* Character count when maxLength is set */}
      {maxLength && <div className="flex justify-end">
          <span className={`text-xs ${value.length > maxLength ? "text-red-500" : "text-ds-text-disabled dark:text-ds-text-muted"}`}>
            {value.length}/{maxLength}
          </span>
        </div>}

      {/* Pill popover — shown when a variable pill is clicked */}
      <VariablePillPopover pill={activePill} onDelete={() => {
      if (activePill) {
        removeVariableSpan(activePill);
        setActivePill(null);
      }
    }} onClose={() => setActivePill(null)} />

      {/* Using variables from - show source nodes when variables are used */}
      {usedVariableSources.length > 0 && <div className="scrollbar-none flex items-center gap-1.5 overflow-x-auto">
          <span className="micro-label shrink-0">Using</span>
          {usedVariableSources.map(source => <span key={source.nodeId} className="text-2xs inline-flex shrink-0 items-center rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 font-medium text-blue-700 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300">
              {source.displayName}
            </span>)}
        </div>}
    </div>;
}